import type { FlightSegment } from '@elara/domain/models/schemas/flight-schemas';

import type { BaseItineraryBlockProps, ItineraryItemAction } from './BaseItineraryBlock.js';

import { useItineraryState, useTripsState, useUserProfileState } from '#states/hooks.js';
import { ImageGalleryWidget } from '#ui/chat/ImageGalleryWidget.js';
import { SimpleMarkdown } from '#ui/chat/SimpleMarkdown.js';
import { DestinationTips } from '#ui/guides/destination-guide.js';

import { BaseItineraryBlock } from './BaseItineraryBlock.js';

export function DestinationArrivalBlock({ event, items, size = 'default', className }: BaseItineraryBlockProps) {
  const itineraryState = useItineraryState();
  const tripsState = useTripsState();
  const userProfile = useUserProfileState();

  if (items.length === 0) return null;

  const primaryItem = items[0]!;

  if (event.type !== 'destination_arrival' || primaryItem.type !== 'flight') return null;

  const flightOption = primaryItem.flightOption;
  const outboundSegments = flightOption?.outboundSegments ?? flightOption?.segments ?? [];

  const firstSegment = outboundSegments.at(0);
  const lastSegment = outboundSegments.at(-1);

  if (!firstSegment || !lastSegment || !lastSegment.destination) return null;

  const thisSegment = event.metadata?.segment as FlightSegment;

  const isEventLastSegment = thisSegment?.destination?.iata_code === lastSegment.destination.iata_code;
  if (!isEventLastSegment) return null;

  const originCountry = firstSegment.origin?.country_code;
  const destinationCountry = lastSegment.destination.country_code;
  const userCountry = userProfile.profile?.countryCode;

  const isNewCountry =
    originCountry && destinationCountry && originCountry !== destinationCountry && destinationCountry !== userCountry;

  if (!isNewCountry) return null;

  const countryCode = destinationCountry;
  const city = lastSegment.destination.city_name;

  const destination = itineraryState.getDestinationForArrival({ countryCode, city });
  const destinationId = destination?.id;

  if (!destination) return null;

  const showGuide = () => destinationId && tripsState.showDestinationGuide(destinationId);
  const actions: ItineraryItemAction[] = [
    { type: 'primary', label: 'Destination guide', icon: 'map', onClick: showGuide },
  ];

  if (size === 'sm' || size === 'default') {
    return (
      <BaseItineraryBlock
        event={event}
        items={items}
        size={size}
        actions={actions}
        className={className}
      ></BaseItineraryBlock>
    );
  }

  const hasImages = destination.imagesForGallery.length > 0;

  return (
    <BaseItineraryBlock
      title={destination?.name ?? event.title}
      event={event}
      items={items}
      size={size}
      className={className}
      panelContent={
        hasImages ? (
          <ImageGalleryWidget
            images={destination.imagesForGallery}
            aspect="fill"
            round="none"
            maxImages={1}
            autoplay={true}
          />
        ) : undefined
      }
    >
      {destination?.shortDescription && (
        <div className="max-w-prose">
          <SimpleMarkdown text={destination.shortDescription} />
        </div>
      )}

      <div className="max-w-prose border-y print:border-y-0">
        <DestinationTips destination={destination} />
      </div>
    </BaseItineraryBlock>
  );
}
